import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { Subject, takeUntil } from 'rxjs';

import { TwcPopupComponent } from '@topcon/twc-angular';

import { JobSiteService, ToastService, TranslationService } from '@mirage-projects/cw-commons';
import { JobSite } from '@mirage-projects/cw-models';

import { KeyValue } from '../../models/key-value';

/**
 * Component that shows the job site settings dialog
 */
@Component({
    selector: 'cw-job-site-edit',
    templateUrl: './job-site-edit.component.html',
    styleUrls: ['./job-site-edit.component.scss'],
    standalone: false,
})
export class JobSiteEditComponent implements OnInit, OnDestroy {
    /** The job site to edit */
    @Input() jobSite!: JobSite;

    @ViewChild('popup') popup!: TwcPopupComponent;

    snapshotTypeOptions!: { label: string; value: string }[];
    loading = false;
    nameDescMaxLength = 255;
    nameRegex = '[\\s\\S]*';
    areSummaryValuesChanged = false;
    form!: FormGroup;
    isUpdateConfirmed = false;

    private destroy$ = new Subject<void>();

    constructor(
        private jobSiteService: JobSiteService,
        private toastService: ToastService,
        private translationService: TranslationService
    ) {}

    ngOnInit(): void {
        this.snapshotTypeOptions = this.jobSiteService.snapshotTypeOptions();
        this.initializeForm();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Set the selected coordinate reference system
     * @param value Selected CRS
     */
    setSelectedCRS(value: KeyValue): void {
        this.form.controls['horizProjectionEPSG'].markAsDirty();
        this.form.patchValue({
            horizProjectionEPSG: value?.key ? `EPSG:${value?.key}` : '',
        });
    }

    /**
     * Returns true if sumary changes have been validated
     */
    areSummaryChangesValid(): boolean {
        return this.areSummaryValuesChanged && this.isUpdateConfirmed;
    }

    /**
     * Toggle the confirmation of the summary changes
     */
    toggleConfirmation(): void {
        this.isUpdateConfirmed = !this.isUpdateConfirmed;
    }

    /**
     * Check if the summary fields have been changed
     */
    checkSummaryValuesChanged(): void {
        const keys = this.getSummaryKeys();

        this.areSummaryValuesChanged = keys.some((key) => this.form?.controls?.[key]?.dirty);
    }

    /**
     * Update the job site
     */
    updateJobSite(): void {
        this.loading = true;
        const jobSiteData = this.getUpdatedData();

        this.jobSiteService.updateJobSite(this.jobSite.id, jobSiteData).subscribe({
            next: () => {
                this.popup.onClose();
                this.showMessage(false);
            },
            error: () => {
                this.showMessage(true);
            },
            complete: () => {
                this.loading = false;
            },
        });
    }

    /**
     * Returns true if the form is valid and dirty
     */
    isSaveEnabled(): boolean {
        return this.form.dirty && this.form.valid && (!this.areSummaryValuesChanged || this.isUpdateConfirmed);
    }

    private initializeForm(): void {
        const { name, description, snapshotType, sampleDistance, horizProjectionEPSG } = this.jobSite;

        this.form = new FormGroup({
            name: new FormControl(name, Validators.required),
            description: new FormControl(description),
            snapshotType: new FormControl(snapshotType),
            sampleDistance: new FormControl(sampleDistance),
            horizProjectionEPSG: new FormControl(horizProjectionEPSG),
        });

        this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((formData) => {
            this.restorePristineState(formData);
            this.checkSummaryValuesChanged();
            this.updateConfirmation();
        });
    }

    private restorePristineState(formData: Partial<JobSite>): void {
        Object.keys(formData).forEach((key) => {
            if (this.form.controls[key].value === this.jobSite[key as keyof JobSite]) {
                this.form.controls[key].markAsPristine();
            }
        });
    }

    private getSummaryKeys(): string[] {
        const nonSummaryKeys = ['name', 'description'];

        return Object.keys(this.form.controls).filter((key) => !nonSummaryKeys.includes(key));
    }

    private updateConfirmation(): void {
        if (!this.areSummaryChangesValid()) {
            this.isUpdateConfirmed = false;
        }
    }

    private getUpdatedData(): Partial<JobSite> {
        return Object.keys(this.form.controls)
            .filter((key) => this.form.controls[key].dirty)
            .reduce((result, key) => Object.assign(result, { [key]: this.form.controls[key].value }), {});
    }

    private showMessage(isError: boolean): void {
        const translationKey = isError ? 'JOB_SITE.EDIT.ERROR' : 'JOB_SITE.EDIT.SUCCESS';
        const translationValues = isError ? { jobSite: this.jobSite.name } : { jobSite: this.form.controls['name'].value };
        const message = this.translationService.translate(translationKey, translationValues);
        const type = isError ? 'error' : 'generic';

        this.toastService[type](message);
    }
}
